import React from 'react';
import { AlertTriangle, Shield, Globe, Users, BookOpen, ArrowLeft } from 'lucide-react';

const About = ({ onBack }) => {
  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 max-w-screen-sm mx-auto">
      <header className="sticky top-0 bg-white dark:bg-gray-900 border-b dark:border-gray-700 z-10 shadow-sm">
        <div className="px-4 py-3">
          <div className="flex items-center justify-between">
            <button 
              onClick={onBack}
              className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
              aria-label="Go back"
            >
              <ArrowLeft className="w-5 h-5 text-gray-600 dark:text-gray-400" />
            </button>
            <h1 className="text-xl font-bold text-gray-900 dark:text-white">About</h1>
            <div className="w-9"></div> {/* Spacer for center alignment */}
          </div>
        </div>
      </header>

      <div className="p-4 space-y-6">
        {/* Mission */}
        <div className="bg-white dark:bg-gray-800 rounded-lg p-4 shadow-sm">
          <div className="flex items-center gap-3 mb-3">
            <Globe className="w-6 h-6 text-blue-600" />
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Our Mission</h2>
          </div>
          <p className="text-gray-700 dark:text-gray-300 text-sm leading-relaxed">
            We help travelers avoid legal trouble by providing quick access to critical laws across 100+ countries. 
            Every year, thousands of tourists face fines, imprisonment, or deportation for unknowingly breaking local laws.
            Our goal is to prevent these situations through education and awareness.
          </p>
        </div>

        {/* Data Sources */}
        <div className="bg-white dark:bg-gray-800 rounded-lg p-4 shadow-sm">
          <div className="flex items-center gap-3 mb-3">
            <BookOpen className="w-6 h-6 text-green-600" />
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Data Sources</h2>
          </div>
          <div className="text-sm space-y-2">
            <p className="text-gray-700 dark:text-gray-300">Our information comes from:</p>
            <ul className="text-gray-600 dark:text-gray-400 space-y-1 ml-4">
              <li>• Official government websites and legal databases</li>
              <li>• Embassy and consulate travel advisories</li>
              <li>• Department of State travel warnings</li>
              <li>• Local customs and immigration authorities</li>
              <li>• Verified news reports of tourist legal issues</li>
            </ul>
            <p className="text-gray-600 dark:text-gray-400 text-xs mt-3">
              <strong>Last Updated:</strong> January 2025 | <strong>Next Review:</strong> April 2025
            </p>
          </div>
        </div>

        {/* Team */}
        <div className="bg-white dark:bg-gray-800 rounded-lg p-4 shadow-sm">
          <div className="flex items-center gap-3 mb-3">
            <Users className="w-6 h-6 text-purple-600" />
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Our Team</h2>
          </div>
          <div className="text-sm text-gray-700 dark:text-gray-300 space-y-2">
            <p>Built by experienced travelers who've encountered legal issues abroad firsthand.</p>
            <p><strong>Research Team:</strong> Travel journalists, legal researchers, and expat communities worldwide</p>
            <p><strong>Technical Team:</strong> Software engineers focused on travel safety technology</p>
            <p><strong>Advisory Board:</strong> International travel lawyers and embassy staff (informal consultation)</p>
          </div>
        </div>

        {/* Methodology */}
        <div className="bg-white dark:bg-gray-800 rounded-lg p-4 shadow-sm">
          <div className="flex items-center gap-3 mb-3">
            <Shield className="w-6 h-6 text-orange-600" />
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Our Methodology</h2>
          </div>
          <div className="text-sm text-gray-700 dark:text-gray-300 space-y-2">
            <p><strong>Focus Areas:</strong> Laws most commonly violated by tourists</p>
            <p><strong>Severity Classification:</strong></p>
            <ul className="ml-4 space-y-1 text-gray-600 dark:text-gray-400">
              <li>• ❌ Illegal - Criminal penalties, fines, imprisonment</li>
              <li>• ⚠️ Restricted - Permits required, conditions apply</li>
              <li>• ✅ Legal - Generally allowed with standard precautions</li>
            </ul>
            <p><strong>Verification Process:</strong> Cross-reference multiple official sources, fact-check with local contacts</p>
          </div>
        </div>

        {/* Critical Disclaimer */}
        <div className="bg-red-50 dark:bg-red-900/30 border-2 border-red-200 dark:border-red-800 rounded-lg p-4">
          <div className="flex gap-3">
            <AlertTriangle className="w-6 h-6 text-red-600 dark:text-red-400 flex-shrink-0" />
            <div>
              <h3 className="text-lg font-bold text-red-900 dark:text-red-200 mb-2">⚖️ LEGAL DISCLAIMER</h3>
              <div className="text-sm text-red-800 dark:text-red-300 space-y-2">
                <p><strong>THIS IS NOT LEGAL ADVICE.</strong> We are not lawyers and this information is for general guidance only.</p>
                <p><strong>Laws change frequently</strong> and our information may be outdated, incomplete, or incorrect.</p>
                <p><strong>Regional variations exist</strong> - what's legal in one city may be illegal in another.</p>
                <p><strong>Always verify current laws</strong> with official government sources before traveling.</p>
                <p><strong>We disclaim all liability</strong> for legal consequences of your actions based on this information.</p>
                <p><strong>For specific legal advice,</strong> consult with a qualified attorney in the relevant jurisdiction.</p>
              </div>
            </div>
          </div>
        </div>

        {/* Contact */}
        <div className="bg-white dark:bg-gray-800 rounded-lg p-4 shadow-sm">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">Contact & Feedback</h2>
          <div className="text-sm text-gray-700 dark:text-gray-300 space-y-2">
            <p><strong>Found incorrect information?</strong> Please help us improve by reporting errors.</p>
            <p><strong>Missing country/law?</strong> We're continuously expanding our database.</p>
            <p><strong>General inquiries:</strong> Contact through our website feedback form.</p>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-3">
              Due to the legal nature of our content, we cannot provide personal legal advice via email.
            </p>
          </div>
        </div>

        {/* Version Info */}
        <div className="bg-gray-100 dark:bg-gray-700 rounded-lg p-3 text-center">
          <p className="text-xs text-gray-600 dark:text-gray-400">
            <strong>App Version:</strong> 2.1.0 | <strong>Database Version:</strong> 2025.01 | 
            <strong>Countries:</strong> 100+ | <strong>Categories:</strong> 6
          </p>
        </div>
      </div>
    </div>
  );
};

export default About;